import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Quotations from "@/pages/Quotations";
import QuotationDetail from "@/pages/QuotationDetail";
import QuotationPrint from "@/pages/QuotationPrint";
import Equipment from "@/pages/Equipment";
import EquipmentLabel from "@/pages/EquipmentLabel";
import ScanView from "@/pages/ScanView";
import AuditTrail from "@/pages/AuditTrail";
import Users from "@/pages/Users";
import { Loader2 } from "lucide-react";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} richColors position="top-right" />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/scan/:id" element={<ScanView />} />
            <Route path="/quotations/:id/print" element={<Protected><QuotationPrint /></Protected>} />
            <Route path="/equipment/:id/label" element={<Protected><EquipmentLabel /></Protected>} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/customers" element={<Protected roles={["admin", "quality_manager", "technical_manager", "supervisor", "technician"]}><Customers /></Protected>} />
              <Route path="/quotations" element={<Quotations />} />
              <Route path="/quotations/:id" element={<QuotationDetail />} />
              <Route path="/equipment" element={<Equipment />} />
              <Route path="/audit" element={<Protected roles={["admin", "quality_manager"]}><AuditTrail /></Protected>} />
              <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AppToaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
